import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { catchError } from "rxjs/operators";
import {
  Organization,
  BusinessUnit,
  AssetGroup,
  Plant,
} from "../interfaces/asset-hierarchy/asset-hierarchy.models";
import { PlantHierarchy } from "../interfaces/asset-hierarchy/plant-hierarchy.interface";
import { environment } from "../../environments/environment";

@Injectable({
  providedIn: "root",
})
export class AssetHierarchyService {
  private baseUrl = environment.apiUrl + "/asset-hierarchy";

  constructor(private http: HttpClient) {}
  getOrganizations(): Observable<Organization[]> {
    const url = `${this.baseUrl}/organization`;

    return this.http.get<Organization[]>(url).pipe(
      catchError((error) => {
        console.error("Error loading organizations:", error);
        return of([]);
      })
    );
  }

  getBusinessUnitsByOrganizationId(
    organizationId: number
  ): Observable<BusinessUnit[]> {
    return this.http
      .get<BusinessUnit[]>(`${this.baseUrl}/business-unit/${organizationId}`)
      .pipe(
        catchError((error) => {
          return of([]);
        })
      );
  }

  getAssetGroupsByBusinessUnitId(
    businessUnitId: number
  ): Observable<AssetGroup[]> {
    return this.http
      .get<AssetGroup[]>(`${this.baseUrl}/asset-group/${businessUnitId}`)
      .pipe(
        catchError((error) => {
          return of([]);
        })
      );
  }
  getPlantsByAssetGroupId(assetGroupId: number): Observable<Plant[]> {
    return this.http.get<Plant[]>(`${this.baseUrl}/plant/${assetGroupId}`).pipe(
      catchError((error) => {
        return of([]);
      })
    );
  }

  getPlantHierarchy(plantId: number): Observable<PlantHierarchy | null> {
    return this.http
      .get<PlantHierarchy>(`${this.baseUrl}/plant-hierarchy/${plantId}`)
      .pipe(
        catchError((error) => {
          console.error("Error loading plant hierarchy:", error);
          return of(null);
        })
      );
  }

  // Create methods
  createOrganization(name: string): Observable<Organization> {
    return this.http
      .post<Organization>(`${this.baseUrl}/organization`, { name })
      .pipe(
        catchError((error) => {
          console.error("Service error creating organization:", error);
          throw error;
        })
      );
  }

  createBusinessUnit(
    name: string,
    organizationId: number
  ): Observable<BusinessUnit> {
    return this.http
      .post<BusinessUnit>(`${this.baseUrl}/business-unit`, {
        name,
        organizationId,
      })
      .pipe(
        catchError((error) => {
          console.error("Service error creating business unit:", error);
          throw error;
        })
      );
  }

  createAssetGroup(
    name: string,
    businessUnitId: number
  ): Observable<AssetGroup> {
    return this.http
      .post<AssetGroup>(`${this.baseUrl}/asset-group`, {
        name,
        businessUnitId,
      })
      .pipe(
        catchError((error) => {
          console.error("Service error creating asset group:", error);
          throw error;
        })
      );
  }

  createPlant(name: string, assetGroupId: number): Observable<Plant> {
    return this.http
      .post<Plant>(`${this.baseUrl}/plant`, { name, assetGroupId })
      .pipe(
        catchError((error) => {
          console.error("Service error creating plant:", error);
          throw error;
        })
      );
  }
}
